import React, { useState, useRef } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import {
  Upload, FileSpreadsheet, Check, AlertTriangle, Download, ArrowRight, ArrowLeft, X,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Dialog reutilizable para importar CSV con mapeo configurable.
 *
 * Props:
 * - open, onOpenChange — estándar de Dialog
 * - tipo: 'empresas' | 'contactos' | 'productos' | 'usuarios'
 * - titulo: string
 * - onCompleted: callback cuando se importa de verdad (para refrescar lista)
 */
const ImportadorCSV = ({ open, onOpenChange, tipo, titulo, onCompleted }) => {
  const [step, setStep] = useState(1); // 1 upload, 2 mapeo, 3 preview, 4 completed
  const [archivo, setArchivo] = useState(null);
  const [columnas, setColumnas] = useState([]);
  const [muestra, setMuestra] = useState([]);
  const [totalFilas, setTotalFilas] = useState(0);
  const [campos, setCampos] = useState({});
  const [mapeo, setMapeo] = useState({}); // campo_interno → columna_csv
  const [preview, setPreview] = useState(null);
  const [resultado, setResultado] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [modoLocalidad, setModoLocalidad] = useState(false);
  const fileRef = useRef();

  const reset = () => {
    setStep(1); setArchivo(null); setColumnas([]); setMuestra([]);
    setTotalFilas(0); setMapeo({}); setPreview(null); setResultado(null); setErr('');
    if (fileRef.current) fileRef.current.value = '';
  };

  const token = () => localStorage.getItem('token');

  const subirYDetectar = async (file) => {
    setLoading(true); setErr('');
    try {
      // Cargar campos disponibles
      const cFields = await fetch(`${API_URL}/api/importacion/${tipo}/campos`, {
        headers: { Authorization: `Bearer ${token()}` },
      }).then((r) => r.json());
      setCampos(cFields.campos || {});

      const fd = new FormData();
      fd.append('archivo', file);
      const res = await fetch(`${API_URL}/api/importacion/${tipo}/detectar`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token()}` },
        body: fd,
      });
      if (!res.ok) throw new Error((await res.json()).detail || `Error ${res.status}`);
      const data = await res.json();
      setColumnas(data.columnas || []);
      setMuestra(data.muestra || []);
      setTotalFilas(data.total_filas || 0);

      // Heurística: auto-mapear si el nombre coincide (case insensitive)
      const auto = {};
      Object.keys(cFields.campos || {}).forEach((campo) => {
        const match = (data.columnas || []).find(
          (col) => col.toLowerCase().replace(/\s|_|-/g, '')
            === campo.toLowerCase().replace(/\s|_|-/g, '')
        );
        if (match) auto[campo] = match;
      });
      setMapeo(auto);

      setArchivo(file);
      setStep(2);
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  };

  const ejecutarDryRun = async () => {
    setLoading(true); setErr('');
    try {
      const fd = new FormData();
      fd.append('archivo', archivo);
      fd.append('mapeo', JSON.stringify(mapeo));
      fd.append('dry_run', 'true');
      if (tipo === 'empresas' && modoLocalidad) fd.append('modo_localidad', 'true');
      const res = await fetch(`${API_URL}/api/importacion/${tipo}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token()}` },
        body: fd,
      });
      if (!res.ok) throw new Error((await res.json()).detail || `Error ${res.status}`);
      setPreview(await res.json());
      setStep(3);
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  };

  const [autoEnriquecerIA, setAutoEnriquecerIA] = useState(false);

  const confirmar = async () => {
    setLoading(true); setErr('');
    try {
      const fd = new FormData();
      fd.append('archivo', archivo);
      fd.append('mapeo', JSON.stringify(mapeo));
      fd.append('dry_run', 'false');
      if (tipo === 'empresas' && modoLocalidad) fd.append('modo_localidad', 'true');
      if (tipo === 'contactos' && autoEnriquecerIA) fd.append('auto_enriquecer_ia', 'true');
      const res = await fetch(`${API_URL}/api/importacion/${tipo}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token()}` },
        body: fd,
      });
      if (!res.ok) throw new Error((await res.json()).detail || `Error ${res.status}`);
      setResultado(await res.json());
      setStep(4);
      if (onCompleted) onCompleted();
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  };

  const handleFile = (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.csv')) {
      setErr('Solo archivos .csv por ahora (UTF-8 recomendado, separador ; o ,)');
      return;
    }
    subirYDetectar(f);
  };

  const required = tipo === 'empresas'
    ? ['razon_social', 'provincia']
    : tipo === 'productos'
      ? ['referencia', 'descripcion']
      : tipo === 'usuarios'
        ? ['email', 'nombre']
        : ['nombre'];
  const faltanRequeridos = required.filter((k) => !mapeo[k]);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { reset(); onOpenChange(false); } }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid={`dialog-import-${tipo}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="w-5 h-5 text-brand-600" /> {titulo}
          </DialogTitle>
          <DialogDescription>
            {step === 1 && 'Sube un archivo CSV con los datos a importar. Podrás mapear las columnas después.'}
            {step === 2 && `${totalFilas} filas detectadas. Asigna a cada campo del CRM la columna del CSV correspondiente.`}
            {step === 3 && 'Revisa el resumen antes de confirmar la importación.'}
            {step === 4 && 'Importación completada.'}
          </DialogDescription>
        </DialogHeader>

        <StepsIndicator step={step} />

        {/* Paso 1: Upload */}
        {step === 1 && (
          <div className="py-4">
            <label className="block border-2 border-dashed border-ink-200 rounded-lg p-10 text-center cursor-pointer hover:border-brand-400 hover:bg-brand-50/30 transition-colors" data-testid="importador-dropzone">
              <FileSpreadsheet className="w-12 h-12 text-ink-300 mx-auto mb-3" />
              <p className="text-sm font-semibold text-ink-800 mb-1">Haz click para seleccionar un CSV</p>
              <p className="text-xs text-ink-500">UTF-8 preferido · separadores: coma, punto-y-coma, tabulador</p>
              <input
                ref={fileRef}
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={(e) => handleFile(e.target.files[0])}
                data-testid="importador-file"
              />
            </label>
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md text-xs text-blue-700">
              <strong>Tip:</strong> exporta tu ERP a CSV y sube el archivo. En el siguiente paso mapearás cada columna a su campo del CRM.
              {tipo === 'empresas' && <> Debe incluir al menos: <strong>razón social</strong> y <strong>provincia</strong> (nombre oficial, p.ej. "Madrid", "Barcelona").</>}
              {tipo === 'contactos' && <> Debe incluir al menos <strong>nombre</strong>. Si incluyes <strong>CIF de la empresa</strong> o <strong>nombre de la empresa</strong>, los contactos se enlazan automáticamente.</>}
              {tipo === 'productos' && <> Debe incluir al menos <strong>referencia</strong> (clave única, se normaliza a mayúsculas) y <strong>descripción</strong>.</>}
              {tipo === 'usuarios' && <> Debe incluir al menos <strong>email</strong> (clave única de match) y <strong>nombre</strong>. Si no se indica contraseña para usuarios nuevos, se asignará <code className="px-1 bg-white border rounded">Cambiar1234!</code> — recuerda cambiarla desde <em>Reset password</em>.</>}
            </div>
          </div>
        )}

        {/* Paso 2: Mapeo */}
        {step === 2 && (
          <div className="py-2">
            {tipo === 'empresas' && (
              <div className="mb-3 p-3 bg-sky-50 border border-sky-200 rounded-md text-xs text-sky-900">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={modoLocalidad}
                    onChange={(e) => setModoLocalidad(e.target.checked)}
                    className="mt-0.5 accent-sky-600"
                    data-testid="toggle-modo-localidad"
                  />
                  <div>
                    <div className="font-semibold">🔎 Mi CSV trae localidades en lugar de provincias</div>
                    <p className="text-sky-800 mt-0.5 leading-snug">
                      Activa si tu columna "provincia" contiene valores como "Rubí", "Porto" o "Pamplona" (en lugar de "Barcelona", "Porto" distrito o "Navarra"). El CRM resolverá la provincia/distrito y el país automáticamente usando la base de datos INE + el CIF. Las filas que no se puedan resolver con certeza se marcarán como "pendientes de revisión" para completar después con Perplexity.
                    </p>
                  </div>
                </label>
              </div>
            )}
            {faltanRequeridos.length > 0 && (
              <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-md text-xs text-amber-800 inline-flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Faltan por mapear: <span className="font-semibold">{faltanRequeridos.map((k) => campos[k]).join(', ')}</span>
              </div>
            )}
            <table className="w-full text-sm">
              <thead className="bg-ink-50 text-ink-600 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold">Campo del CRM</th>
                  <th className="text-left px-3 py-2 font-semibold">Columna del CSV</th>
                  <th className="text-left px-3 py-2 font-semibold">Ejemplo</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100">
                {Object.entries(campos).map(([campo, label]) => {
                  const esReq = required.includes(campo);
                  const col = mapeo[campo] || '';
                  return (
                    <tr key={campo} data-testid={`map-row-${campo}`}>
                      <td className="px-3 py-2">
                        <span className={esReq ? 'font-semibold text-ink-900' : 'text-ink-700'}>{label}</span>
                      </td>
                      <td className="px-3 py-2">
                        <select
                          value={col}
                          onChange={(e) => setMapeo({ ...mapeo, [campo]: e.target.value })}
                          className={`w-full h-9 rounded-md border text-sm px-2 ${
                            esReq && !col ? 'border-amber-400 bg-amber-50' : 'border-ink-200 bg-white'
                          }`}
                          data-testid={`map-select-${campo}`}
                        >
                          <option value="">— Ignorar —</option>
                          {columnas.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </td>
                      <td className="px-3 py-2 text-xs text-ink-500 font-mono truncate max-w-[200px]">
                        {col && muestra[0] ? muestra[0][col] : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Paso 3: Preview */}
        {step === 3 && preview && (
          <div className="py-4 space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-center">
                <p className="text-2xl font-bold text-emerald-700">{preview.crear}</p>
                <p className="text-xs text-emerald-600 uppercase tracking-wider">Nuevos</p>
              </div>
              <div className="p-3 rounded-lg bg-blue-50 border border-blue-200 text-center">
                <p className="text-2xl font-bold text-blue-700">{preview.actualizar}</p>
                <p className="text-xs text-blue-600 uppercase tracking-wider">Actualizados</p>
              </div>
              <div className={`p-3 rounded-lg ${preview.errores.length ? 'bg-red-50 border border-red-200' : 'bg-ink-100 border border-ink-200'} text-center`}>
                <p className={`text-2xl font-bold ${preview.errores.length ? 'text-red-700' : 'text-ink-500'}`}>{preview.errores.length}</p>
                <p className={`text-xs uppercase tracking-wider ${preview.errores.length ? 'text-red-600' : 'text-ink-500'}`}>Errores</p>
              </div>
            </div>
            {preview.errores.length > 0 && (
              <div className="border border-red-200 rounded-md bg-red-50/50 p-3 max-h-40 overflow-y-auto">
                <p className="text-xs font-semibold text-red-700 mb-1">Filas con errores (se ignorarán):</p>
                <ul className="text-xs text-red-600 space-y-0.5 font-mono">
                  {preview.errores.slice(0, 15).map((e, i) => (
                    <li key={i}>Fila {e.fila}: {e.error}</li>
                  ))}
                  {preview.errores.length > 15 && <li>… y {preview.errores.length - 15} más</li>}
                </ul>
              </div>
            )}
            {preview.sin_empresa > 0 && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-md text-xs text-amber-800 inline-flex gap-2">
                <AlertTriangle className="w-4 h-4 shrink-0" />
                {preview.sin_empresa} contactos se crearán <strong>sin empresa asignada</strong> (no se encontró match por CIF/nombre). Podrás asignarles una después.
              </div>
            )}
            {tipo === 'contactos' && preview.crear > 0 && (
              <label className="flex items-start gap-2 p-3 rounded-md border border-violet-200 bg-violet-50/40 cursor-pointer text-xs text-violet-900" data-testid="import-auto-enriquecer-ia-wrap">
                <input
                  type="checkbox"
                  className="mt-0.5 accent-violet-600"
                  checked={autoEnriquecerIA}
                  onChange={(e) => setAutoEnriquecerIA(e.target.checked)}
                  data-testid="import-auto-enriquecer-ia"
                />
                <span>
                  <strong>Auto-enriquecer con Perplexity</strong> tras importar
                  <span className="block text-violet-700 mt-0.5">
                    Encolará los {preview.crear} contactos nuevos para completar cargo, departamento, LinkedIn y email vía búsqueda IA. El proceso es asíncrono — puedes seguir trabajando.
                  </span>
                </span>
              </label>
            )}
            {preview.pendientes_revision > 0 && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-md text-xs text-amber-800 flex gap-2">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <div>
                  <strong>{preview.pendientes_revision}</strong> empresas se crearán marcadas como <strong>pendientes de revisión</strong> porque la localidad no se pudo resolver con certeza (ambigua o desconocida). Podrás filtrarlas en <em>/clientes</em> y completarlas más tarde (o automáticamente con Perplexity cuando esté conectado).
                </div>
              </div>
            )}
            {preview.muestra_creados?.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-ink-700 mb-1">Muestra de nuevos:</p>
                <ul className="text-xs text-ink-600 space-y-0.5 font-mono">
                  {preview.muestra_creados.map((m, i) => (
                    <li key={i}>
                      Fila {m.fila}: {m.razon_social || m.nombre}
                      {m.cif ? ` · ${m.cif}` : ''}
                      {m.email ? ` · ${m.email}` : ''}
                      {m.provincia ? ` · ${m.provincia}` : ''}
                      {m.pais && m.pais !== 'ES' ? ` · ${m.pais}` : ''}
                      {m.pendiente_revision ? ' ⚠️' : ''}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Paso 4: Completed */}
        {step === 4 && resultado && (
          <div className="py-6 text-center">
            <div className="w-14 h-14 rounded-full bg-emerald-100 text-emerald-600 mx-auto flex items-center justify-center mb-3">
              <Check className="w-7 h-7" />
            </div>
            <h3 className="font-heading font-bold text-ink-900 text-lg">¡Importación completada!</h3>
            <p className="text-sm text-ink-600 mt-1">
              <span className="font-semibold text-emerald-700">{resultado.crear} nuevos</span> ·
              <span className="font-semibold text-blue-700"> {resultado.actualizar} actualizados</span>
              {resultado.errores.length > 0 && <> · <span className="text-red-700">{resultado.errores.length} errores</span></>}
            </p>
          </div>
        )}

        {err && <div className="p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">{err}</div>}

        <DialogFooter className="flex-wrap gap-2">
          {step === 2 && (
            <>
              <Button type="button" variant="outline" onClick={() => setStep(1)} className="mr-auto">
                <ArrowLeft className="w-4 h-4 mr-1" /> Volver
              </Button>
              <Button
                type="button"
                className="bg-brand-500 hover:bg-brand-600 text-white"
                onClick={ejecutarDryRun}
                disabled={loading || faltanRequeridos.length > 0}
                data-testid="btn-importador-preview"
              >
                {loading ? 'Analizando…' : 'Previsualizar'} <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </>
          )}
          {step === 3 && (
            <>
              <Button type="button" variant="outline" onClick={() => setStep(2)} className="mr-auto">
                <ArrowLeft className="w-4 h-4 mr-1" /> Ajustar mapeo
              </Button>
              <Button
                type="button"
                className="bg-emerald-500 hover:bg-emerald-600 text-white"
                onClick={confirmar}
                disabled={loading || (preview?.crear === 0 && preview?.actualizar === 0)}
                data-testid="btn-importador-confirmar"
              >
                {loading ? 'Importando…' : `Confirmar (${(preview?.crear || 0) + (preview?.actualizar || 0)} registros)`}
              </Button>
            </>
          )}
          {step === 4 && (
            <Button type="button" className="bg-brand-500 hover:bg-brand-600 text-white ml-auto" onClick={() => { reset(); onOpenChange(false); }} data-testid="btn-importador-cerrar">
              Cerrar
            </Button>
          )}
          {step === 1 && (
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const StepsIndicator = ({ step }) => {
  const labels = ['Archivo', 'Mapeo', 'Previsualizar', 'Completado'];
  return (
    <div className="flex items-center justify-center gap-2 py-2">
      {labels.map((l, i) => {
        const done = step > i + 1;
        const active = step === i + 1;
        return (
          <div key={l} className="flex items-center gap-2">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
              done ? 'bg-emerald-500 text-white' : active ? 'bg-brand-500 text-white' : 'bg-ink-200 text-ink-500'
            }`}>{done ? <Check className="w-3.5 h-3.5" /> : i + 1}</div>
            <span className={`text-xs ${active ? 'font-semibold text-ink-900' : 'text-ink-500'}`}>{l}</span>
            {i < labels.length - 1 && <div className={`w-4 h-0.5 ${done ? 'bg-emerald-500' : 'bg-ink-200'}`} />}
          </div>
        );
      })}
    </div>
  );
};

export default ImportadorCSV;
